import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  Upload, 
  Download, 
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  FileUp,
  RefreshCw
} from 'lucide-react';

const ImportExportPage = () => {
  const { api, canManageUsers } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    // Redirect non-admin users
    if (!canManageUsers()) {
      navigate('/decisions');
    }
  }, [canManageUsers, navigate]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api().post('/import/excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult({
        success: true,
        ...response.data
      });
      toast.success('Import completed successfully');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      setImportResult({
        success: false,
        error: error.response?.data?.detail || 'Import failed'
      });
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api().get('/export/excel', {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'NSR_Decision_Log.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="import-export">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground font-serif">Import / Export</h1>
        <p className="text-muted-foreground mt-1">
          Manage decision data through Excel files
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Import Card */}
        <Card className="paper-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" />
              Import from Excel
            </CardTitle>
            <CardDescription>
              Upload an Excel file to update decisions. Existing decisions will be updated, 
              new ones will be created, and removed ones will be archived.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* File Input Area */}
            <div 
              className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              data-testid="file-drop-area"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              {selectedFile ? (
                <div>
                  <p className="font-medium text-foreground">{selectedFile.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              ) : (
                <div>
                  <p className="font-medium">Click to select Excel file</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Supports .xlsx and .xls formats
                  </p>
                </div>
              )}
            </div>

            {/* Import Button */}
            <Button
              className="w-full"
              onClick={handleImport}
              disabled={!selectedFile || importing}
              data-testid="import-btn"
            >
              {importing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <FileUp className="w-4 h-4 mr-2" />
                  Import Decisions
                </>
              )}
            </Button>

            {/* Import Result */}
            {importResult && (
              <Alert variant={importResult.success ? 'default' : 'destructive'} data-testid="import-result">
                {importResult.success ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <AlertCircle className="h-4 w-4" />
                )}
                <AlertTitle>
                  {importResult.success ? 'Import Completed' : 'Import Failed'}
                </AlertTitle>
                <AlertDescription>
                  {importResult.success ? (
                    <div className="mt-2 space-y-1">
                      <p>• {importResult.imported || 0} decisions imported</p>
                      <p>• {importResult.updated || 0} decisions updated</p>
                      <p>• {importResult.archived || 0} decisions archived</p>
                      {importResult.errors?.length > 0 && (
                        <div className="mt-2 text-orange-600">
                          <p className="font-medium">Warnings:</p>
                          {importResult.errors.slice(0, 5).map((err, i) => (
                            <p key={i} className="text-sm">{err}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p>{importResult.error}</p>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Format Info */}
            <div className="bg-muted/50 rounded-lg p-4 text-sm">
              <p className="font-medium mb-2">Expected Format:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Sheets named by category (Baseline, Dynamic Fields, etc.)</li>
                <li>Decision ID column must be unique</li>
                <li>Standard column headers (Decision Question, Statement, etc.)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Export Card */}
        <Card className="paper-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-primary" />
              Export to Excel
            </CardTitle>
            <CardDescription>
              Download all decisions as an Excel workbook with separate sheets 
              per category and a reference sheet.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Export Preview */}
            <div className="bg-muted/50 rounded-lg p-6">
              <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-primary" />
              <h3 className="font-medium text-center mb-4">Export Contents</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Baseline (BL)</span>
                  <span className="text-muted-foreground">Sheet 1</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Dynamic Fields (DY)</span>
                  <span className="text-muted-foreground">Sheet 2</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Integration Strategy (IS)</span>
                  <span className="text-muted-foreground">Sheet 3</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Rollout/Resourcing (RC)</span>
                  <span className="text-muted-foreground">Sheet 4</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Policy/Governance (GV)</span>
                  <span className="text-muted-foreground">Sheet 5</span>
                </div>
                <div className="flex items-center justify-between text-sm border-t pt-2 mt-2">
                  <span>Reference</span>
                  <span className="text-muted-foreground">Sheet 6</span>
                </div>
              </div>
            </div>

            {/* Export Button */}
            <Button
              className="w-full"
              onClick={handleExport}
              disabled={exporting}
              data-testid="export-btn"
            >
              {exporting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export All Decisions
                </>
              )}
            </Button>

            {/* Export Info */}
            <div className="bg-muted/50 rounded-lg p-4 text-sm">
              <p className="font-medium mb-2">Export Includes:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>All decision fields including status</li>
                <li>Formatted headers with Zambia green styling</li>
                <li>Reference sheet with export metadata</li>
                <li>Decision dates for validated items</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ImportExportPage;
